import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import {
    ArrowLeft,
    BadgeCheck,
    FileSearch,
    RefreshCw,
    ShieldAlert,
} from "lucide-react";
import {Link, useParams} from "react-router-dom";
import {
    AUTH_EXPIRED_EVENT,
    SECURITY_EXIT_EVENT,
    expireAuthSession,
} from "../../shared/api/client";
import {formatDateTime} from "../../shared/utils/format";
import {
    getApplication,
    getHistory,
    mapOnboardingError,
    onboardingKeys,
    publishApplication,
    rejectApplication,
    requestDocuments,
    rotateAccessToken,
    rotateCredentials,
} from "./api";
import {useBusinessOnboardingWorkspace} from "./BusinessOnboardingWorkspace";
import {ApplicationSummary} from "./components/ApplicationSummary";
import {DocumentList} from "./components/DocumentList";
import {ModerationActions} from "./components/ModerationActions";
import {OneTimeSecretDialog} from "./components/OneTimeSecretDialog";
import {PublishedResources} from "./components/PublishedResources";
import {RequiredCommentDialog} from "./components/RequiredCommentDialog";
import {StatusHistory} from "./components/StatusHistory";
import {
    extractAccessTokenSecret,
    extractCredentialSecrets,
    extractPublishSecrets,
    normalizeActionResponse,
} from "./mappers";
import {getStatusPresentation} from "./statusMachine";
import {useTranslation, i18n} from "../../shared/i18n";


const FLOW_LABELS = {
    get independent() { return i18n.t("sections.onboarding.p1"); },
    get sales_invite() { return i18n.t("sections.onboarding.p2"); },
};

const isPositiveApplicationId = (value) => /^[1-9]\d*$/.test(value);

const DetailState = ({title, copy, requestId, retry}) => (
    <section className="onboarding-detail-state" aria-labelledby="onboarding-detail-state-title">
        <span aria-hidden="true">
            <FileSearch size={24} />
        </span>
        <h1 id="onboarding-detail-state-title">{title}</h1>
        <p>{copy}</p>
        {requestId ? <code>Request ID: {requestId}</code> : null}
        <div>
            <Link className="admin-button admin-button-secondary" to="/business-onboarding">
                <ArrowLeft aria-hidden="true" size={16} />
                {i18n.t("sections.onboarding.p3")}
            </Link>
            {retry ? (
                <button className="admin-button admin-button-primary" type="button" onClick={retry}>
                    <RefreshCw aria-hidden="true" size={16} />
                    {i18n.t("sections.onboarding.p4")}
                </button>
            ) : null}
        </div>
    </section>
);

const DetailSkeleton = () => (
    <div className="onboarding-detail-layout" role="status">
        <span className="onboarding-sr-only">{i18n.t("sections.onboarding.p5")}</span>
        <div className="onboarding-detail-skeleton" data-testid="onboarding-detail-skeleton">
            {Array.from({length: 8}, (_, index) => (
                <span key={index} />
            ))}
        </div>
        <aside
            className="onboarding-action-rail onboarding-action-rail-skeleton"
            data-testid="onboarding-action-rail-skeleton"
        >
            <span />
            <span />
            <span />
        </aside>
    </div>
);

const IDLE_MUTATION = {
    status: "idle",
    actionId: null,
    error: null,
    uncertain: false,
    canRetry: false,
    retryAvailableAt: 0,
    dialogError: false,
    replayWithoutSecret: false,
};

const SECRET_ACTIONS = new Set(["publish", "rotate_access_token", "rotate_credentials"]);
const FORBIDDEN_DETAIL_KEY = ["admin-forbidden", "application"];
const FORBIDDEN_HISTORY_KEY = ["admin-forbidden", "history"];

const ActionRail = ({application, disabled, mutationState, onOpenAction, onRetry, onRefresh}) => {
    const {t} = useTranslation();
    const status = getStatusPresentation(application.status);
    return (
        <aside
            className="onboarding-action-rail"
            aria-label={t("sections.onboarding.p6")}
            tabIndex="-1"
        >
            <p className="onboarding-section-kicker">{t("sections.onboarding.p7")}</p>
            <span className={`onboarding-status onboarding-status-${status.tone}`}>
                {status.label}
            </span>
            <h2>{t("sections.onboarding.p8")}</h2>
            <p className="onboarding-action-meaning">{status.meaning}</p>

            {!status.known ? (
                <div className="onboarding-rail-warning">
                    <ShieldAlert aria-hidden="true" size={17} />
                    <span>{t("sections.onboarding.p9")}</span>
                </div>
            ) : null}

            {application.blocking_issues.length > 0 ? (
                <div className="onboarding-rail-blockers">
                    <h3>{t("sections.onboarding.p10")}</h3>
                    <ul>
                        {application.blocking_issues.map((issue) => (
                            <li key={`${issue.code}-${issue.field || "general"}`}>
                                <strong>{issue.message}</strong>
                                {issue.field ? <small>{issue.field}</small> : null}
                            </li>
                        ))}
                    </ul>
                </div>
            ) : (
                <div className="onboarding-rail-clear">
                    <BadgeCheck aria-hidden="true" size={17} />
                    {t("sections.onboarding.p11")}
                </div>
            )}

            {application.action_evidence ? (
                <div className="onboarding-last-action">
                    <h3>{t("sections.onboarding.p12")}</h3>
                    <strong>{application.action_evidence.actor_name}</strong>
                    <span>{application.action_evidence.actor_role}</span>
                    <time dateTime={application.action_evidence.created_at}>
                        {formatDateTime(application.action_evidence.created_at)}
                    </time>
                    <code>{application.action_evidence.request_id}</code>
                </div>
            ) : null}

            <ModerationActions
                application={application}
                disabled={disabled}
                mutationState={mutationState}
                onOpenAction={onOpenAction}
                onRetry={onRetry}
                onRefresh={onRefresh}
            />
        </aside>
    );
};

export const ApplicationDetailPage = () => {
    const {t} = useTranslation();
    const {applicationId} = useParams();
    const validApplicationId = isPositiveApplicationId(applicationId);
    const numericApplicationId = validApplicationId ? Number(applicationId) : null;
    const {countryScope} = useBusinessOnboardingWorkspace();
    const queryClient = useQueryClient();
    const [historyPage, setHistoryPage] = useState(1);
    const [historyEvents, setHistoryEvents] = useState([]);
    const [historyCount, setHistoryCount] = useState(0);
    const [historyNext, setHistoryNext] = useState(null);
    const [actionDialog, setActionDialog] = useState(null);
    const [mutationState, setMutationState] = useState(IDLE_MUTATION);
    const [forbidden, setForbidden] = useState(false);
    const [secretEnvelope, setSecretEnvelope] = useState(null);
    const pendingRequestRef = useRef(null);
    const activeCommandRef = useRef(null);
    const detailKey = onboardingKeys.application(countryScope, numericApplicationId);
    const historyKey = onboardingKeys.history(
        countryScope,
        numericApplicationId,
        historyPage,
    );

    const abortActiveCommand = useCallback(() => {
        const activeCommand = activeCommandRef.current;
        activeCommandRef.current = null;
        activeCommand?.controller.abort();
    }, []);

    const beginCommand = () => {
        abortActiveCommand();
        const owner = {
            controller: new AbortController(),
            applicationId: numericApplicationId,
            countryScope: String(countryScope),
        };
        activeCommandRef.current = owner;
        return owner;
    };

    const ownsCommand = (owner) => activeCommandRef.current === owner;

    const releaseCommand = (owner) => {
        if (ownsCommand(owner)) activeCommandRef.current = null;
    };

    const clearSensitiveState = useCallback(
        ({removeCachedEvidence = false} = {}) => {
            abortActiveCommand();
            pendingRequestRef.current = null;
            setSecretEnvelope(null);
            setActionDialog(null);
            setMutationState(IDLE_MUTATION);
            setHistoryEvents([]);
            setHistoryCount(0);
            setHistoryNext(null);
            if (removeCachedEvidence) {
                void queryClient.cancelQueries({queryKey: onboardingKeys.all});
                queryClient.removeQueries({queryKey: onboardingKeys.all});
            }
        },
        [abortActiveCommand, queryClient],
    );

    const enterForbiddenState = useCallback(
        (error) => {
            clearSensitiveState({removeCachedEvidence: true});
            setForbidden(true);
            setMutationState({...IDLE_MUTATION, status: "error", error});
        },
        [clearSensitiveState],
    );

    const detailQuery = useQuery({
        queryKey: forbidden ? FORBIDDEN_DETAIL_KEY : detailKey,
        queryFn: () =>
            getApplication({applicationId: numericApplicationId, countryScope}),
        enabled: validApplicationId && Boolean(countryScope) && !forbidden,
        retry: false,
        refetchInterval: false,
    });

    const historyQuery = useQuery({
        queryKey: forbidden ? FORBIDDEN_HISTORY_KEY : historyKey,
        queryFn: () =>
            getHistory({
                applicationId: numericApplicationId,
                countryScope,
                page: historyPage,
            }),
        enabled: validApplicationId && Boolean(countryScope) && !forbidden,
        retry: false,
        refetchInterval: false,
    });

    useEffect(() => {
        setHistoryPage(1);
        setHistoryEvents([]);
        setHistoryCount(0);
        setHistoryNext(null);
        setForbidden(false);
        setActionDialog(null);
        setMutationState(IDLE_MUTATION);
        setSecretEnvelope(null);
        pendingRequestRef.current = null;
        return abortActiveCommand;
    }, [abortActiveCommand, countryScope, numericApplicationId]);

    useEffect(() => {
        if (!historyQuery.data) return;
        setHistoryEvents((current) => {
            if (historyPage === 1) return historyQuery.data.results;
            const knownIds = new Set(current.map((event) => event.id));
            return [
                ...current,
                ...historyQuery.data.results.filter((event) => !knownIds.has(event.id)),
            ];
        });
        setHistoryCount(historyQuery.data.count);
        setHistoryNext(historyQuery.data.next);
    }, [historyPage, historyQuery.data]);

    useEffect(() => {
        const clearPageSecrets = () => clearSensitiveState();
        const clearSessionEvidence = () =>
            clearSensitiveState({removeCachedEvidence: true});
        window.addEventListener("pagehide", clearPageSecrets);
        window.addEventListener(AUTH_EXPIRED_EVENT, clearSessionEvidence);
        window.addEventListener(SECURITY_EXIT_EVENT, clearSessionEvidence);
        return () => {
            window.removeEventListener("pagehide", clearPageSecrets);
            window.removeEventListener(AUTH_EXPIRED_EVENT, clearSessionEvidence);
            window.removeEventListener(SECURITY_EXIT_EVENT, clearSessionEvidence);
        };
    }, [clearSensitiveState]);

    useEffect(() => {
        const clearCountryEvidence = () => {
            clearSensitiveState({removeCachedEvidence: true});
        };
        window.addEventListener("sorpa-admin-country-scope-change", clearCountryEvidence);
        return () =>
            window.removeEventListener("sorpa-admin-country-scope-change", clearCountryEvidence);
    }, [clearSensitiveState]);

    const detailError = useMemo(
        () => (detailQuery.isError ? mapOnboardingError(detailQuery.error) : null),
        [detailQuery.error, detailQuery.isError],
    );
    const historyError = useMemo(
        () => (historyQuery.isError ? mapOnboardingError(historyQuery.error) : null),
        [historyQuery.error, historyQuery.isError],
    );

    useEffect(() => {
        if (detailError) setSecretEnvelope(null);
    }, [detailError]);

    useEffect(() => {
        if (detailError?.kind === "unauthorized" || historyError?.kind === "unauthorized") {
            setSecretEnvelope(null);
            expireAuthSession();
        }
    }, [detailError?.kind, historyError?.kind]);

    useEffect(() => {
        if (detailError?.kind === "forbidden" || historyError?.kind === "forbidden") {
            enterForbiddenState(
                detailError?.kind === "forbidden" ? detailError : historyError,
            );
        }
    }, [detailError, enterForbiddenState, historyError]);

    const invalidateCountryQueues = () =>
        queryClient.invalidateQueries({
            predicate: ({queryKey}) =>
                queryKey[0] === "business-onboarding" &&
                queryKey[1] === "applications" &&
                queryKey[2] === String(countryScope),
        });

    const commandArgs = (requestRecord, signal) => ({
        applicationId: numericApplicationId,
        countryScope,
        comment: requestRecord.body.comment || undefined,
        revision: requestRecord.revision,
        idempotencyKey: requestRecord.idempotencyKey,
        ...(signal ? {signal} : {}),
    });

    const refreshApplicationEvidence = async () => {
        await Promise.all([detailQuery.refetch(), historyQuery.refetch()]);
    };

    const handleMutationError = async (error, requestRecord) => {
        const mapped = mapOnboardingError(error);
        const baseState = {
            status: "error",
            actionId: requestRecord.actionId,
            error: mapped,
            uncertain: false,
            canRetry: false,
            retryAvailableAt: 0,
            dialogError: false,
            replayWithoutSecret: false,
        };

        if (mapped.kind === "network" || mapped.kind === "server") {
            setActionDialog(null);
            setMutationState({...baseState, uncertain: true, canRetry: true});
            return;
        }

        if (mapped.kind === "rate_limited") {
            const retryAvailableAt = mapped.retryAfterSeconds
                ? Date.now() + mapped.retryAfterSeconds * 1000
                : 0;
            setActionDialog(null);
            setMutationState({...baseState, canRetry: true, retryAvailableAt});
            return;
        }

        pendingRequestRef.current = null;

        if (mapped.kind === "unauthorized") {
            setSecretEnvelope(null);
            setActionDialog(null);
            setMutationState(baseState);
            expireAuthSession();
            return;
        }

        if (mapped.kind === "forbidden") {
            enterForbiddenState(mapped);
            return;
        }

        if (mapped.code === "stale_revision" || mapped.code === "invalid_status") {
            setActionDialog(null);
            setMutationState(baseState);
            await refreshApplicationEvidence();
            return;
        }

        setActionDialog((current) =>
            current
                ? {...current, initialComment: requestRecord.body.comment}
                : {
                      action: requestRecord.actionId,
                      capturedRevision: requestRecord.revision,
                      initialComment: requestRecord.body.comment,
                  },
        );
        setMutationState({...baseState, dialogError: true});
    };

    const runNonSecretRequest = async (requestRecord) => {
        const owner = beginCommand();
        pendingRequestRef.current = requestRecord;
        setMutationState({
            ...IDLE_MUTATION,
            status: "pending",
            actionId: requestRecord.actionId,
        });
        try {
            const command =
                requestRecord.actionId === "request_documents"
                    ? requestDocuments
                    : rejectApplication;
            const raw = await command(commandArgs(requestRecord, owner.controller.signal));
            if (!ownsCommand(owner)) return;
            const result = normalizeActionResponse(raw);
            const committed = await commitPublicResult(result, () => ownsCommand(owner));
            if (!committed || !ownsCommand(owner)) return;
            releaseCommand(owner);
            pendingRequestRef.current = null;
            setActionDialog(null);
            setMutationState({
                ...IDLE_MUTATION,
                status: "success",
                actionId: requestRecord.actionId,
            });
        } catch (error) {
            if (!ownsCommand(owner)) return;
            releaseCommand(owner);
            await handleMutationError(error, requestRecord);
        }
    };

    const commitPublicResult = async (
        {application, applicationPatch, evidence},
        isCurrent = () => true,
    ) => {
        if (!isCurrent()) return false;
        const previousDetail = queryClient.getQueryData(detailKey);
        const committedDetail = queryClient.setQueryData(detailKey, (current) =>
            application || {...current, ...applicationPatch, action_evidence: evidence},
        );
        setHistoryPage(1);
        setHistoryEvents([]);
        setHistoryCount(0);
        setHistoryNext(null);
        await Promise.all([
            invalidateCountryQueues(),
            queryClient.invalidateQueries({
                queryKey: onboardingKeys.historyRoot(countryScope, numericApplicationId),
            }),
        ]);
        if (!isCurrent()) {
            if (queryClient.getQueryData(detailKey) === committedDetail) {
                if (previousDetail === undefined) {
                    queryClient.removeQueries({queryKey: detailKey, exact: true});
                } else {
                    queryClient.setQueryData(detailKey, previousDetail);
                }
            }
            return false;
        }
        return true;
    };

    const runSecretCommand = async (requestRecord) => {
        const owner = beginCommand();
        pendingRequestRef.current = requestRecord;
        setSecretEnvelope(null);
        setMutationState({
            ...IDLE_MUTATION,
            status: "pending",
            actionId: requestRecord.actionId,
        });
        try {
            const command = {
                publish: publishApplication,
                rotate_access_token: rotateAccessToken,
                rotate_credentials: rotateCredentials,
            }[requestRecord.actionId];
            const raw = await command(commandArgs(requestRecord, owner.controller.signal));
            if (!ownsCommand(owner)) return;
            const extracted =
                requestRecord.actionId === "publish"
                    ? extractPublishSecrets(raw)
                    : requestRecord.actionId === "rotate_access_token"
                      ? extractAccessTokenSecret(raw)
                      : extractCredentialSecrets(raw);
            const committed = await commitPublicResult(
                extracted.publicResult,
                () => ownsCommand(owner),
            );
            if (!committed || !ownsCommand(owner)) return;
            releaseCommand(owner);
            pendingRequestRef.current = null;
            setActionDialog(null);
            setSecretEnvelope(extracted.secretEnvelope);
            setMutationState({
                ...IDLE_MUTATION,
                status: "success",
                actionId: requestRecord.actionId,
                replayWithoutSecret: extracted.secretEnvelope === null,
            });
        } catch (error) {
            if (!ownsCommand(owner)) return;
            releaseCommand(owner);
            await handleMutationError(error, requestRecord);
        }
    };

    const runRequest = (requestRecord) => {
        if (SECRET_ACTIONS.has(requestRecord.actionId)) {
            runSecretCommand(requestRecord);
        } else {
            runNonSecretRequest(requestRecord);
        }
    };

    const openAction = (actionId) => {
        if (mutationState.status === "pending" || detailQuery.isFetching) return;
        setMutationState(IDLE_MUTATION);
        setActionDialog({
            action: actionId,
            capturedRevision: detailQuery.data.revision,
            initialComment: "",
        });
    };

    const confirmAction = (comment) => {
        if (!actionDialog || !window.crypto?.randomUUID) return;
        const requestRecord = {
            actionId: actionDialog.action,
            body: {comment: comment || undefined},
            revision: actionDialog.capturedRevision,
            idempotencyKey: window.crypto.randomUUID(),
        };
        runRequest(requestRecord);
    };

    const retrySameRequest = () => {
        if (pendingRequestRef.current) runRequest(pendingRequestRef.current);
    };

    if (!validApplicationId) {
        return (
            <DetailState
                title={t("sections.onboarding.p13")}
                copy={t("sections.onboarding.p14")}
            />
        );
    }

    if (forbidden) {
        return (
            <DetailState
                title={t("sections.onboarding.p15")}
                copy={t("sections.onboarding.p16")}
                requestId={mutationState.error?.requestId}
            />
        );
    }

    if (detailQuery.isLoading) return <DetailSkeleton />;

    if (detailError) {
        if (detailError.kind === "not_found") {
            return (
                <DetailState
                    title={t("sections.onboarding.p13")}
                    copy={detailError.message}
                    requestId={detailError.requestId}
                />
            );
        }
        if (detailError.kind === "forbidden") {
            return (
                <DetailState
                    title={t("sections.onboarding.p15")}
                    copy={detailError.message}
                    requestId={detailError.requestId}
                />
            );
        }
        if (detailError.kind === "unauthorized") {
            return (
                <DetailState
                    title={t("sections.onboarding.p17")}
                    copy={detailError.message}
                    requestId={detailError.requestId}
                />
            );
        }
        return (
            <DetailState
                title={
                    detailError.kind === "contract"
                        ? t("sections.onboarding.p18")
                        : t("sections.onboarding.p19")
                }
                copy={detailError.message}
                requestId={detailError.requestId}
                retry={() => detailQuery.refetch()}
            />
        );
    }

    const detail = detailQuery.data;
    const status = getStatusPresentation(detail.status);
    const applicationName = detail.display_name || detail.company_name || t("sections.onboarding.p20", {v0: detail.id});

    return (
        <div className="admin-page onboarding-detail-page">
            <Link className="onboarding-back-link" to="/business-onboarding">
                <ArrowLeft aria-hidden="true" size={16} />
                {t("sections.onboarding.p21")}
            </Link>

            <header className="onboarding-detail-header">
                <div>
                    <div className="onboarding-detail-title-line">
                        <p>{t("sections.onboarding.p22")}{detail.id}</p>
                        <span className={`onboarding-status onboarding-status-${status.tone}`}>
                            {status.label}
                        </span>
                    </div>
                    <h1>{applicationName}</h1>
                    <p>{detail.company_name || t("sections.onboarding.p23")}</p>
                </div>
                <dl className="onboarding-detail-header-meta">
                    <div>
                        <dt>{t("sections.onboarding.p24")}</dt>
                        <dd>{formatDateTime(detail.submitted_at)}</dd>
                    </div>
                    <div>
                        <dt>{t("sections.onboarding.p25")}</dt>
                        <dd>{formatDateTime(detail.updated_at)}</dd>
                    </div>
                    <div>
                        <dt>{t("sections.onboarding.p26")}</dt>
                        <dd>
                            {[detail.city_name, detail.country_name].filter(Boolean).join(", ") ||
                                t("sections.onboarding.p27")}
                        </dd>
                    </div>
                    <div>
                        <dt>{t("sections.onboarding.p28")}</dt>
                        <dd>{FLOW_LABELS[detail.flow_type] || detail.flow_type}</dd>
                    </div>
                </dl>
            </header>

            {detailQuery.isFetching ? (
                <div className="onboarding-detail-refresh" role="progressbar" />
            ) : null}

            <div className="onboarding-detail-layout">
                <main className="onboarding-detail-main">
                    <ApplicationSummary application={detail} />
                    <DocumentList
                        applicationId={numericApplicationId}
                        countryScope={countryScope}
                        documents={detail.documents}
                        onForbidden={enterForbiddenState}
                    />
                    <PublishedResources status={detail.status} resources={detail.resources} />
                    <StatusHistory
                        events={historyEvents}
                        count={historyCount}
                        hasOlder={!historyError && historyNext !== null}
                        initialLoading={historyQuery.isLoading && historyEvents.length === 0}
                        loadingOlder={historyQuery.isFetching && historyEvents.length > 0}
                        error={historyError}
                        onLoadOlder={() => setHistoryPage(historyNext)}
                        onRetry={() => historyQuery.refetch()}
                    />
                </main>
                <ActionRail
                    application={detail}
                    disabled={detailQuery.isFetching}
                    mutationState={mutationState}
                    onOpenAction={openAction}
                    onRetry={retrySameRequest}
                    onRefresh={refreshApplicationEvidence}
                />
            </div>
            <RequiredCommentDialog
                open={Boolean(actionDialog)}
                action={actionDialog?.action}
                applicationName={applicationName}
                status={detail.status}
                capturedRevision={actionDialog?.capturedRevision}
                currentRevision={detail.revision}
                pending={mutationState.status === "pending"}
                error={mutationState.dialogError ? mutationState.error : null}
                initialComment={actionDialog?.initialComment || ""}
                onCancel={() => {
                    if (mutationState.status !== "pending") {
                        pendingRequestRef.current = null;
                        setActionDialog(null);
                        setMutationState(IDLE_MUTATION);
                    }
                }}
                onConfirm={confirmAction}
            />
            <OneTimeSecretDialog
                envelope={secretEnvelope}
                onClose={() => setSecretEnvelope(null)}
            />
        </div>
    );
};
